import { atom, atomFamily } from "recoil";
import { atomWithPersistence, localStorageEffect } from "../utils/persistence";
import { TimesheetEntry } from "../types/timesheet";
import { LatLng, Uid } from "../types/core";
import { ColumnState } from 'ag-grid-community';

export type Requesting = {
    req: "timeline" | "tracks" | "routecalc" | "alljobs" | "stops" | "timesheet" | "places" | "jobtemplates" | "activities"
    uid?: string;
    day?: number;
}

export const requestingState = atomFamily<boolean, Requesting>({
    key: 'requesting',
    default: false,
});

export const routingStatusState = atomFamily({
    key: "routing.status",
    default: true,
});

export const routingFailureReasonState = atomFamily<string | undefined, { uid: string; day: number }>({
    key: "routing.failure.reason",
    default: undefined,
});

export const timesheetByDayState = atomFamily<TimesheetEntry[], number>({
    key: 'timesheet',
    default: [] as TimesheetEntry[],
});

export const timesheetEventState = atom({
    key: 'timesheet.event',
    default: 0,
});

export const formSubmissionEventState = atom({
    key: "formsubmission.event",
    default: 0,
})


export const newMemberEventState = atom({
    key: 'newmember.event',
    default: 0,
});

export const globalDatePickerHighlightedState = atom({ 'key': "globaldatepicker.highlighted", 'default': false });

export type AllJobsActiveTab = "jobs" | "archived";

export const allJobsActiveTabState = atom<AllJobsActiveTab>({
    key: "alljobs.view.activetab",
    default: "jobs",
});

export const passwordRecoveryActivationState = atom<boolean>({
    key: "passwordrecovery.activation",
    default: false,
});

export type DispatchTreeActiveCard =
    /** only in sessions saved when Co-Dispatch was a card of the board; DispatchView opens the panel instead */
    "autopilot"
    | "unassigned"
    | "jobs"
    | "jobgroups"
    | "templates"
    | "clustering"
    | "multiroute"
    | "pinned"
    | "recurring"
    | undefined;

export const dispatchTreeActiveCardState = atom<DispatchTreeActiveCard>({
    key: "dispatchtree.view.activecard",
    default: undefined,
});



export const columnTitleReorderDialogState = atom<"orderId" | "priority" | undefined>({
    key: "view.column.title.reorder.dialog",
    default: undefined,
});

export type ExpandableSection = {
    sectionKey: string;
    defaultExpanded: boolean;
}

export const expandableSectionState = atomFamily<boolean, ExpandableSection>({
    key: 'expanded',
    default: (section) => section.defaultExpanded,
    effects: [localStorageEffect],
});

export const allJobsRowCountState = atom({
    key: "view.alljobs.row.count",
    default: 0,
});

export const multiMemberSelectViewState = atom<string[]>({
    key: "view.multimemberselect",
    default: [],
});

export const routingMatrixDurationsState = atom<{ [key: Uid]: number }>({
    key: "view.routing.matrix.durations",
    default: {},
});

export const routingMatrixRequestedState = atom<boolean>({
    key: "view.routing.matrix.requested",
    default: false,
});

export const compoundKeyDirections = (uid: string | null, stops: LatLng[]) => JSON.stringify({ uid, stops });

export const routingDirectionsState = atomFamily<GeoJSON.Feature | undefined, string>({
    key: "view.routing.directions",
    default: () => undefined,
});

export const routingHoverState = atom<string | undefined>({
    key: "view.routing.hover",
    default: undefined,
});

export const routingSelectedState = atom<string | undefined>({
    key: "view.routing.selected",
    default: undefined,
});

export const unbouncedRoutingHoverState = atom<string | undefined>({
    key: "view.routing.hover.unbounced",
    default: undefined,
});

export const findAssigneeForJobState = atom<boolean>({
    key: "view.find.assignee",
    default: false,
});

/** The dispatch people list shown as avatars beside an open route; the browser keeps the choice, like the menu's pin. */
export const dispatchListNarrowState = atomWithPersistence("view.dispatch.list.narrow", false);

export const jobsGridCompactState = atomWithPersistence("view.jobs.grid.compact", false);



// Column configuration state for ZoneGrid and PlaceGrid
export const locationGridColumnConfigState = atomWithPersistence<ColumnState[] | undefined>("view.location.grid.columns", undefined);
/** the Stops list's own columns, so hiding or moving one there leaves the trips table alone */
export const locationStopsGridColumnConfigState = atomWithPersistence<ColumnState[] | undefined>("view.location.stops.grid.columns", undefined);

// Location type filter state (persisted)
export interface LocationTypeFilterState {
    showTracks: boolean;
    showStopsAtPlaces: boolean;
    showStopsAtJobs: boolean;
    showStopsIdling: boolean;
}

export const locationTypeFilterState = atomWithPersistence<LocationTypeFilterState>("view.location.type.filter", {
    showTracks: true,
    showStopsAtPlaces: true,
    showStopsAtJobs: true,
    showStopsIdling: true,
});

export const showMemberTagsState = atomWithPersistence("view.member.tags.show", true);
export const useSectionsInMemberGridState = atomWithPersistence("view.member.grid.sectioned", true);

